import { Button, FormControl, MenuItem, TextField } from "@mui/material";
import {
	AttachedFiles,
	AttachFile,
	DefaultSupportedFiles,
	DescribeYourProblemText,
	MaxFileSize,
	MaxFileSizeHeader,
	PerImage,
	PleaseDescribeYourProblemErrorText,
	PleaseSelectReplacementReasonErrorText,
	SelectReplacementReasonText,
	SupportedFilesHeader
} from "constants/text";
import { REASONS_FOR_REPLACEMENT, REPLACEMENT_REASON_CODES } from "constants/replacementReasonCodes";
import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { cartActions, Part, ReplacementCartItem } from "features/reducers/replacementOrder/cart.ts";
import { useDispatch } from "react-redux";
import UploadModal, { ErrorableFileState } from "components/UploadModal/UploadModal";
import UploadView from "components/UploadView/UploadView";
import UploadFile from "@mui/icons-material/UploadFile";
import { isValidFile, sanitizeBlobName } from "utils/file";
import {
	ImageUploadModalInfoContainer,
	ImageUploadModalInfoText,
	ImageUploadViewContainer,
	ReplacementProblemDropdownOption,
	ReplacementReasonUploadFiles,
	SelectReplacementReasonAndProblemDropdownsContainer,
	SelectThreeImagesText
} from "./SelectReplacementReasonAndProblemDropdowns.styles";
import { useGetLinkedOrdersQuery } from "features/api/orderApi.ts";
import { useParams } from "react-router";
import { OrderType } from "types/declarations/enums.ts";
import AutoComplete from "components/Common/Autocomplete/Autocomplete.tsx";
import { Controller, get, useFormContext } from "react-hook-form";
import {
	HasItemBeenReplacedBeforeErrorText,
	HasItemBeenReplacedBeforeLabel,
	PleaseAttachFileErrorText,
	schema as entrySchema
} from "./SelectReplacementReasonAndProblemDropdowns.schema";
import HelperText from "components/Form/HelperText/HelperText.tsx";
import { getFormFieldProps } from "utils/form.tsx";

interface Props {
	item: ReplacementCartItem;
	part?: Part;
	fieldPrefix: string;
	maxFiles?: number;
}

const SelectReplacementReasonAndProblemDropdowns = ({ item, part, fieldPrefix, maxFiles = 3 }: Props) => {
	const dispatch = useDispatch();
	const [open, setOpen] = useState(false);
	const replacementReason = part ? part.partReplacementReason : item.cabinetReplacementReason;
	const replacementProblem = part ? part.partReplacementProblem : item.cabinetReplacementProblem;
	const fileUploads = useMemo(
		() => (part ? part.partFileUploads : item.cabinetFileUploads) ?? [],
		[item.cabinetFileUploads, part]
	);
	const comment = part ? part.comment : item.comment;
	const partToEdit = useMemo(() => (part ? { ...part } : null), [part]);
	const { orderId } = useParams();
	const { data: linkedOrders } = useGetLinkedOrdersQuery(orderId ?? "");
	const skuToCheck = part ? part.partSku : item.sku;
	const originalHasAnotherReplacementOrder = useMemo(
		() =>
			linkedOrders?.some(
				(order) => order.orderType === OrderType.REPLACEMENT && order.skUs?.includes(skuToCheck)
			) ?? false,
		[linkedOrders, skuToCheck]
	);

	const { clearErrors, control, formState, setValue, watch, register } = useFormContext();

	useEffect(() => {
		setValue(`${fieldPrefix}.originalHasAnotherReplacementOrder`, originalHasAnotherReplacementOrder);
	}, [originalHasAnotherReplacementOrder, setValue, fieldPrefix]);

	useEffect(() => {
		setValue(`${fieldPrefix}.fileUploads`, fileUploads);
	}, [fileUploads, setValue, fieldPrefix]);

	useEffect(() => {
		setValue(`${fieldPrefix}.comment`, comment ?? null);
	}, [comment, setValue, fieldPrefix]);

	const qualityCheckFlagForm = watch(`${fieldPrefix}.qualityCheckFlag`);
	const commentForm = watch(`${fieldPrefix}.comment`) as string | null | undefined;
	const isFileRequired = qualityCheckFlagForm === true;

	const replaceableParts = item.replaceableParts.map((replaceablePart) => {
		if (replaceablePart.id === part?.id) {
			return partToEdit as Part;
		}
		return { ...replaceablePart } as Part;
	});

	const itemToEdit = { ...item, replaceableParts };

	const handleCommentChange = (value: string) => {
		const updated = partToEdit
			? {
					...item,
					replaceableParts: item.replaceableParts.map((p) =>
						p.id === part?.id ? { ...p, comment: value || null } : p
					)
				}
			: { ...item, replaceableParts, comment: value || null };
		dispatch(cartActions.updateItem(updated));
		clearErrors(`${fieldPrefix}.comment`);
	};

	const readAsDataURL = (file: File): Promise<string> =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = reject;
			reader.readAsDataURL(file);
		});

	const dataURLtoBlob = (dataUrl: string): Blob => {
		const commaIndex = dataUrl.indexOf(",");
		const header = dataUrl.slice(0, commaIndex);
		const base64 = dataUrl.slice(commaIndex + 1);
		const mimeStart = header.indexOf(":") + 1;
		const mimeEnd = header.indexOf(";");
		const mime = mimeEnd > mimeStart ? header.slice(mimeStart, mimeEnd) : "application/octet-stream";
		const bytes = atob(base64);
		const arr = new Uint8Array(bytes.length);
		for (let i = 0; i < bytes.length; i++) {
			arr[i] = bytes.charCodeAt(i); // NOSONAR
		}
		return new Blob([arr], { type: mime });
	};

	// On mount: detect stale blob URLs (post-refresh) and restore from sessionStorage if available
	useEffect(() => {
		if (fileUploads.length === 0) return;
		(async () => {
			let anyStale = false;
			const restored = await Promise.all(
				fileUploads.map(async (fu) => {
					if (!fu.fileUrl.startsWith("blob:")) return fu;
					try {
						await fetch(fu.fileUrl);
						return fu;
					} catch {
						anyStale = true;
						const dataUrl = sessionStorage.getItem(`_file_${fu.id}_${fu.name}`);
						if (!dataUrl) return null;
						return { ...fu, fileUrl: URL.createObjectURL(dataURLtoBlob(dataUrl)) };
					}
				})
			);
			if (!anyStale) return;
			const validRestored = restored.filter(Boolean);
			const updated = partToEdit
				? {
						...item,
						replaceableParts: item.replaceableParts.map((p) =>
							p.id === part?.id ? { ...p, partFileUploads: validRestored } : p
						)
					}
				: { ...item, replaceableParts, cabinetFileUploads: validRestored };
			dispatch(cartActions.updateItem(updated));
			setValue(`${fieldPrefix}.fileUploads`, validRestored);
		})();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const handleOpen = () => setOpen(true);
	const handleClose = (files: ErrorableFileState[], item: ReplacementCartItem, part: Part | null) => {
		const validFiles = files.filter(({ file }) => isValidFile(file, fileUploads));
		if (validFiles.length > 0) {
			if (part) {
				const newFiles = validFiles.map(({ file }) => {
					const name = sanitizeBlobName(file.name);
					const fileUrl = URL.createObjectURL(file);
					readAsDataURL(file)
						.then((dataUrl) => {
							try {
								sessionStorage.setItem(`_file_${part.id}_${name}`, dataUrl);
							} catch {
								console.error("Failed to persist data URL to sessionStorage");
							}
						})
						.catch(() => {});
					return { name, size: file.size, fileUrl, id: part.id, originalLineItemId: part.lineItemId! };
				});
				part.partFileUploads = part.partFileUploads.concat(newFiles);
				setValue(`${fieldPrefix}.fileUploads`, part.partFileUploads, { shouldValidate: true });
			} else {
				const newFiles = validFiles.map(({ file }) => {
					const name = sanitizeBlobName(file.name);
					const fileUrl = URL.createObjectURL(file);
					readAsDataURL(file)
						.then((dataUrl) => {
							try {
								sessionStorage.setItem(`_file_${item.id}_${name}`, dataUrl);
							} catch {
								console.error("Failed to persist data URL to sessionStorage");
							}
						})
						.catch(() => {});
					return { name, size: file.size, fileUrl, id: item.id, originalLineItemId: item.lineItemId! };
				});
				item.cabinetFileUploads = item.cabinetFileUploads.concat(newFiles);
				setValue(`${fieldPrefix}.fileUploads`, item.cabinetFileUploads, { shouldValidate: true });
			}

			dispatch(cartActions.updateItem(item));
		}
		setOpen(false);
	};

	const getDefectCode = (replacementProblem: string) => {
		return REPLACEMENT_REASON_CODES.filter((item) => item.reasonDescription === replacementProblem).map(
			(item) => item.reasonCode
		)[0];
	};

	const handleSelectedReplacementReason = (_: SyntheticEvent<Element, Event>, value: any) => {
		if (partToEdit) {
			partToEdit.partReplacementReason = value;
			partToEdit.partReplacementProblem = null;
			partToEdit.partFileUploads = [];
		} else {
			itemToEdit.cabinetReplacementReason = value;
			itemToEdit.cabinetReplacementProblem = null;
			itemToEdit.cabinetFileUploads = [];
		}

		setValue(`${fieldPrefix}.replacementProblem`, null);
		setValue(`${fieldPrefix}.qualityCheckFlag`, null);
		setValue(`${fieldPrefix}.fileUploads`, []);
		setValue(`${fieldPrefix}.comment`, null);
		clearErrors([
			`${fieldPrefix}.replacementReason`,
			`${fieldPrefix}.replacementProblem`,
			`${fieldPrefix}.qualityCheckFlag`,
			`${fieldPrefix}.fileUploads`,
			`${fieldPrefix}.comment`
		]);
		dispatch(cartActions.updateItem(itemToEdit));
	};

	const handleSelectedReplacementProblem = (value: string | null) => {
		if (partToEdit) {
			partToEdit.partReplacementProblem = value;
			partToEdit.partFileUploads = [];
			partToEdit.partDefectCode = getDefectCode(value ?? "") ?? "";
			partToEdit.qualityCheckFlag = null;
		} else {
			itemToEdit.cabinetReplacementProblem = value;
			itemToEdit.cabinetFileUploads = [];
			itemToEdit.cabinetDefectCode = getDefectCode(value ?? "") ?? "";
			itemToEdit.qualityCheckFlag = null;
		}

		setValue(`${fieldPrefix}.qualityCheckFlag`, null);
		setValue(`${fieldPrefix}.fileUploads`, []);
		setValue(`${fieldPrefix}.comment`, null);
		clearErrors([
			`${fieldPrefix}.replacementProblem`,
			`${fieldPrefix}.qualityCheckFlag`,
			`${fieldPrefix}.fileUploads`,
			`${fieldPrefix}.comment`
		]);
		dispatch(cartActions.updateItem(itemToEdit));
	};

	const handleUpdateQualityFlag = (_: SyntheticEvent<Element, Event>, value: any) => {
		if (partToEdit) {
			partToEdit.qualityCheckFlag = value;
		} else {
			itemToEdit.qualityCheckFlag = value;
		}

		setValue(`${fieldPrefix}.fileUploads`, []);
		clearErrors([`${fieldPrefix}.qualityCheckFlag`, `${fieldPrefix}.fileUploads`]);
		dispatch(cartActions.updateItem(itemToEdit));
	};

	const handleDeleteUploadedItem = (index: number, item: ReplacementCartItem, part: Part | null) => {
		if (part) {
			const removed = part.partFileUploads[index];
			sessionStorage.removeItem(`_file_${removed?.id}_${removed?.name}`);
			part.partFileUploads = [...part.partFileUploads.slice(0, index), ...part.partFileUploads.slice(index + 1)];
			setValue(`${fieldPrefix}.fileUploads`, part.partFileUploads, { shouldValidate: true });
		} else {
			const removed = item.cabinetFileUploads[index];
			sessionStorage.removeItem(`_file_${removed?.id}_${removed?.name}`);
			item.cabinetFileUploads = [
				...item.cabinetFileUploads.slice(0, index),
				...item.cabinetFileUploads.slice(index + 1)
			];
			setValue(`${fieldPrefix}.fileUploads`, item.cabinetFileUploads, { shouldValidate: true });
		}

		dispatch(cartActions.updateItem(item));
	};

	return (
		<SelectReplacementReasonAndProblemDropdownsContainer>
			<div>
				<FormControl
					size="small"
					required
					fullWidth
				>
					<Controller
						name={`${fieldPrefix}.replacementReason`}
						control={control}
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<AutoComplete
								options={REASONS_FOR_REPLACEMENT.map((reason) => reason.replacementReason)}
								onChange={(event, val) => {
									onChange(val);
									handleSelectedReplacementReason(event, val);
								}}
								errorText={error?.message ?? PleaseSelectReplacementReasonErrorText}
								label={SelectReplacementReasonText}
								dataTestId="replacementReason-selectReplacementReasonDropdown"
								isError={!!error}
								value={value}
								required
							/>
						)}
					/>
				</FormControl>
			</div>
			{replacementReason && (
				<div>
					<FormControl
						size="small"
						required
						fullWidth
					>
						<Controller
							name={`${fieldPrefix}.replacementProblem`}
							control={control}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<AutoComplete
									options={REPLACEMENT_REASON_CODES.reduce(
										(accumulator: (typeof REPLACEMENT_REASON_CODES)[number][], reason) => {
											const isPart = Boolean(item) && Boolean(part);
											if (
												reason.replacementReason === partToEdit?.partReplacementReason ||
												(!isPart &&
													reason.replacementReason === itemToEdit?.cabinetReplacementReason)
											) {
												accumulator.push(reason);
											}
											return accumulator;
										},
										[]
									)}
									renderOption={(props, option) => (
										<MenuItem
											{...props}
											key={option.id}
											data-testid={`replacementReason-selectReplacementProblemDropdown-${option.reasonDescription}`}
										>
											<ReplacementProblemDropdownOption>
												<span>{option.reasonDescription}</span>
												<span className="body2">{option.reasonSecondaryText}</span>
											</ReplacementProblemDropdownOption>
										</MenuItem>
									)}
									onChange={(_, option) => {
										const reasonDescription = option?.reasonDescription ?? null;
										onChange(reasonDescription);
										handleSelectedReplacementProblem(reasonDescription);
									}}
									getOptionLabel={(option) => option?.reasonDescription}
									errorText={PleaseDescribeYourProblemErrorText}
									label={DescribeYourProblemText}
									dataTestId="replacementReason-selectReplacementProblemDropdown"
									isError={!!error}
									value={REPLACEMENT_REASON_CODES.find((r) => r.reasonDescription === value) ?? null}
									required
								/>
							)}
						/>
					</FormControl>
				</div>
			)}
			{originalHasAnotherReplacementOrder && replacementProblem && (
				<div>
					<FormControl
						size="small"
						required
						fullWidth
					>
						<Controller
							name={`${fieldPrefix}.qualityCheckFlag`}
							control={control}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<AutoComplete
									options={[true, false]}
									onChange={(event, val) => {
										onChange(val);
										handleUpdateQualityFlag(event, val);
									}}
									errorText={error?.message ?? HasItemBeenReplacedBeforeErrorText}
									label={HasItemBeenReplacedBeforeLabel}
									dataTestId="replacement-has-been-replaced-before"
									getOptionLabel={(option) => (option ? "Yes" : "No")}
									isError={!!error}
									value={value}
									required
								/>
							)}
						/>
					</FormControl>
				</div>
			)}

			{((originalHasAnotherReplacementOrder && (qualityCheckFlagForm || qualityCheckFlagForm === false)) ||
				(!originalHasAnotherReplacementOrder && replacementProblem)) && (
				<ReplacementReasonUploadFiles>
					<SelectThreeImagesText>
						{isFileRequired
							? "Please describe the issue compared to the previous replacement. Attaching an image will help us diagnose the cause."
							: "For quicker resolution, please provide a description and up to 3 images of the problem to help our quality team."}
					</SelectThreeImagesText>
					<div>
						<FormControl
							size="small"
							required
							fullWidth
						>
							<Controller
								name={`${fieldPrefix}.comment`}
								control={control}
								render={({ field: { onChange, value } }) => (
									<TextField
										{...getFormFieldProps({
											name: `${fieldPrefix}.comment` as any,
											characterLimit: {
												currentLength: (commentForm as string)?.length ?? 0,
												limit: 500
											},
											formState: formState as any,
											register: register as any,
											schema: entrySchema as any
										})}
										label="Issue Comments"
										placeholder={
											isFileRequired
												? "How similar is the issue to before? Was the problem in the same spot? Etc."
												: "Where is the problem located? Have you seen this problem before? Etc."
										}
										slotProps={{
											inputLabel: {
												shrink: true
											}
										}}
										onChange={(e) => {
											onChange(e.target.value);
											handleCommentChange(e.target.value);
										}}
										value={value ?? ""}
										required={isFileRequired}
										multiline
										rows={3}
										fullWidth
										data-testid="message-designer-input"
									/>
								)}
							/>
						</FormControl>
					</div>
					{fileUploads.length < maxFiles && (
						<Button
							variant="outlined"
							onClick={handleOpen}
							fullWidth
							data-testid="replacements-attachFileButton"
						>
							<UploadFile sx={{ width: "1.25rem", height: "1.25rem", marginRight: ".5rem" }} />
							<div>{AttachFile}</div>
						</Button>
					)}
					{(get(formState.errors, `${fieldPrefix}.fileUploads`) ||
						get(formState.errors, `${fieldPrefix}.fileUploads.root`)) && (
						<div style={{ marginTop: "8px" }}>
							<HelperText
								isError
								text={
									get(formState.errors, `${fieldPrefix}.fileUploads`)?.message ||
									get(formState.errors, `${fieldPrefix}.fileUploads.root`)?.message ||
									PleaseAttachFileErrorText
								}
							/>
						</div>
					)}
					<ImageUploadModalInfoContainer>
						<ImageUploadModalInfoText>
							{SupportedFilesHeader} <span>{DefaultSupportedFiles}</span>
						</ImageUploadModalInfoText>
						<ImageUploadModalInfoText>
							{MaxFileSizeHeader} <span>{MaxFileSize}</span> {PerImage}
						</ImageUploadModalInfoText>
					</ImageUploadModalInfoContainer>
					{!!fileUploads.length && (
						<ImageUploadViewContainer>
							<p>{AttachedFiles}</p>
							{fileUploads.map((fileUrl, index) => (
								<UploadView
									key={fileUrl.id}
									index={index}
									file={fileUrl}
									onImageRemove={() => handleDeleteUploadedItem(index, itemToEdit, partToEdit)}
								/>
							))}
						</ImageUploadViewContainer>
					)}

					<UploadModal
						handleClose={handleClose}
						item={itemToEdit}
						{...(partToEdit === null ? {} : { part: partToEdit })}
						open={open}
						maxFiles={maxFiles - fileUploads.length || 0}
						fileUploads={fileUploads}
					/>
				</ReplacementReasonUploadFiles>
			)}
		</SelectReplacementReasonAndProblemDropdownsContainer>
	);
};

export default SelectReplacementReasonAndProblemDropdowns;
